import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';

import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function IntantQuotesConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Keep this list stable across renders to avoid unnecessary child rerenders
    // For Saved: we keep View, Delete -> remove Create/Edit/Copy/Print
    const removeContextItems = useMemo(() => ['Create', 'Update', 'Print', 'Copy'], []);

    // Centralize route to avoid duplication/typos
    const CRUD_ROUTE = 'instant-quotes/instantQuotesConfigCrud';

    //=============================================================================================================
    // openRateOfferCrud:
    // - Stable (useCallback) handler to open/focus the CRUD tab for Rate Offers (saved quotes).
    // - Persists 'operation' (and row when required) into global state.
    // - Validates presence of a selected row for View and shows a localized toast if missing.
    // - Builds the tab label using i18n keys to avoid leaking label logic to the caller.
    // - Using a stable callback prevents MTable from rerendering/refetching due to changing prop identities.
    //=============================================================================================================
    const openRateOfferCrud = useCallback(
        (operation, row) => {
            // Persist operation for the CRUD screen
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            if (!row) {
                toastMessages('error', '', t('SelectedLine'));
                return;
            }
            // Keep existing global key for downstream CRUD compatibility within RequestForQuotes
            dispatch(M_StateActions.addM_State({ key: 'InstantQuoteLine', value: row }));

            // Only View supported here (Delete is handled by table)
            const tabLabel = t('LabelViewInstantQuote', { name: row?.Name ?? '' });

            // Open/focus CRUD tab
            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab]
    );

    //=============================================================================================================
    // replaceFunctions:
    // - Memoized action map passed to MTable so it won't change identity on every render.
    // - Each action resolves to our stable openRateOfferCrud callback.
    //=============================================================================================================
    // - This reduces unnecessary re renders and duplicate fetches inside MTable.
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openRateOfferCrud('View', row)],
                // Delete is handled by the table's built-in behavior/permissions
            ]),
        [openRateOfferCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("InstantQuotesSaved")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoInstantQuotesSaved")}</div>
                </div>
            </div>
            <MTable // Main table for saved Instant Quotes (rate offers)
                filtersOnTable
                entityName={'RateOffers'}
                roleName={'InstantQuotes'}
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management in the RequestForQuotes Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // replaceFunctions for CRUD operations (View)
                tabsRailWidth={250}
            />
        </div>
    );
}

IntantQuotesConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
